import React, { useCallback, useRef } from 'react'

import { func, number, string } from 'prop-types'

const SEEK_STEP_SECONDS = 1

const clamp = (seconds, duration) => Math.min(Math.max(seconds, 0), Math.max(duration, 0))

const toPercent = (seconds, duration) => (
  `${Math.min(Math.max(duration > 0 ? seconds / duration : 0, 0), 1) * 100}%`
)

function ProgressTrack({
  currentTime,
  duration,
  bufferedSeconds,
  label,
  valueText,
  onSeek: handleSeek,
}) {
  const trackRef = useRef(null)

  const handleClick = useCallback((event) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) {
      return
    }

    const ratio = (event.clientX - rect.left) / rect.width
    handleSeek(Math.min(Math.max(ratio, 0), 1) * duration)
  }, [duration, handleSeek])

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const step = event.key === 'ArrowLeft' ? -SEEK_STEP_SECONDS : SEEK_STEP_SECONDS
      handleSeek(currentTime + step)

      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      handleSeek(0)

      return
    }

    if (event.key === 'End') {
      event.preventDefault()
      handleSeek(duration)
    }
  }, [currentTime, duration, handleSeek])

  return (
    <div
      ref={trackRef}
      className="animation-player__track"
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.floor(duration)}
      // Clamped like the visual track: an overshooting position would otherwise
      // report a valuenow above valuemax, which is an invalid slider state.
      aria-valuenow={Math.floor(clamp(currentTime, duration))}
      aria-valuetext={valueText}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className="animation-player__track-background" />
      <span
        className="animation-player__track-buffered"
        style={{ width: toPercent(bufferedSeconds, duration) }}
      />
      <span
        className="animation-player__track-progress"
        style={{ width: toPercent(currentTime, duration) }}
      />
    </div>
  )
}

ProgressTrack.propTypes = {
  currentTime: number.isRequired,
  duration: number.isRequired,
  bufferedSeconds: number.isRequired,
  label: string.isRequired,
  valueText: string.isRequired,
  onSeek: func.isRequired,
}

export default ProgressTrack
